import { ListingCategory, ListingCondition, SealedCondition, LotType } from './listing';
import type { CardLanguage } from '@/constants/pokemonLanguages';
import type { PokemonErrorType } from '@/constants/pokemonErrors';
import type { PromoSeries } from '@/constants/pokemonPromos';

// Item visibility states for collection-first marketplace
export type ItemVisibility = 'private' | 'showcase' | 'listed';

export type ScEra = 'Sun & Moon' | 'Sword & Shield' | 'Scarlet & Violet';
export type ScProductType = 
  | 'booster_box'        // Covers regular, slim, jumbo
  | 'booster_pack'       // Single packs, blisters
  | 'booster_case'       // Wholesale cases
  | 'etb'                // Elite Trainer Box
  | 'premium_collection' // UPCs, special collections, poster/binder collections
  | 'tin'                // Tins, mini tins, gift boxes
  | 'build_battle'       // Build & Battle / Pre-release
  | 'starter_deck'       // Starter/Theme decks
  | 'bundle'             // Booster bundles, misc box sets
  | 'other';             // Accessories, blind boxes, catch-all

export interface UserItemImage {
  id: string;
  item_id: string;
  image_url: string;
  display_order: number;
  created_at: string;
}

export interface UserItem {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  
  // Product identification
  title: string;
  category: ListingCategory;
  condition: ListingCondition;
  language?: string | null;
  game?: string | null;
  custom_game_name?: string | null;
  set_code?: string | null;
  era?: ScEra | null;
  product_type?: ScProductType | null;
  notes?: string | null;
  set_name?: string | null;
  
  // Item visibility (private, showcase, listed)
  visibility?: ItemVisibility;
  
  // Sealed-specific fields
  sealed_condition?: SealedCondition | null;
  pack_count?: number | null;
  has_original_packaging?: boolean;
  has_receipt?: boolean;
  
  // Slab-specific fields
  slab_grade?: number | null;
  grading_company?: string | null;
  certification_number?: string | null;
  
  // Singles card metadata
  card_number?: string | null;
  rarity?: string | null;
  finish?: string | null;
  finish_label?: string | null;
  pokemon_type?: string | null;
  artist?: string | null;
  is_first_edition?: boolean;
  is_reverse_holo?: boolean;
  
  // Error card fields (singles)
  is_error_card?: boolean;
  error_type?: PokemonErrorType | null;
  
  // Promo card fields (singles)
  is_promo?: boolean;
  promo_series?: PromoSeries | null;
  promo_number?: string | null;
  
  // Ownership details
  quantity: number;
  purchase_date?: string | null;
  cost_basis_cents?: number | null;

  // Prices stashed from the most recent Collectr CSV import.
  // Used by the "Use Collectr price" action — never auto-applied to asking_price.
  collectr_market_price_cents?: number | null;
  collectr_price_override_cents?: number | null;
  
  // Geolocation for distance filtering
  latitude?: number | null;
  longitude?: number | null;
  ships_anywhere?: boolean;
  
  // Marketplace fields
  is_listed: boolean;
  asking_price_cents?: number | null;
  is_negotiable: boolean;
  accepts_trades: boolean;
  description?: string | null;
  city?: string | null;
  state_code?: string | null;
  expires_at?: string | null;
  
  // Future-proofing
  target_price_cents?: number | null;
  sc_product_id?: string | null;
  stock_image_url?: string | null;
  
  // Price drop tracking
  previous_price_cents?: number | null;
  price_dropped_at?: string | null;
  
  // Sold tracking
  sold_at?: string | null;
  sold_to_user_id?: string | null;
  sold_price_cents?: number | null;

  // Pending sale (external channel, e.g. eBay) awaiting seller acknowledgment
  pending_sale_source?: string | null;
  pending_sale_at?: string | null;
  pending_sale_order_ref?: string | null;
  pending_sale_price_cents?: number | null;
  pending_sale_quantity?: number | null;
  pending_prior_visibility?: string | null;
  
  // Per-unit cost tracking for multi-quantity items
  individual_costs_cents?: number[] | null;
  
  // Proof Pack fields
  proof_images?: Record<string, string> | null;
  proof_video_url?: string | null;
  
  has_proof_pack?: boolean;
  
  // Lot-specific fields
  lot_type?: LotType | null;
  approx_card_count?: number | null;
  box_count?: string | null;
  lot_highlights?: string | null;
  lot_eras?: string[] | null;
  lot_languages?: string[] | null;
  includes_holos?: boolean;
  includes_ultras?: boolean;
  includes_slabs?: boolean;
  
  // Joined data
  images?: UserItemImage[];
}

export const ERA_OPTIONS: { value: ScEra; label: string }[] = [
  { value: 'Sun & Moon', label: 'Sun & Moon' },
  { value: 'Sword & Shield', label: 'Sword & Shield' },
  { value: 'Scarlet & Violet', label: 'Scarlet & Violet' },
];

export const PRODUCT_TYPE_OPTIONS: { value: ScProductType; label: string }[] = [
  { value: 'booster_box', label: 'Booster Box' },
  { value: 'booster_pack', label: 'Booster Pack' },
  { value: 'booster_case', label: 'Booster Case' },
  { value: 'etb', label: 'Elite Trainer Box' },
  { value: 'premium_collection', label: 'Premium Collection' },
  { value: 'tin', label: 'Tin / Gift Set' },
  { value: 'build_battle', label: 'Build & Battle' },
  { value: 'starter_deck', label: 'Starter Deck' },
  { value: 'bundle', label: 'Bundle / Box Set' },
  { value: 'other', label: 'Other' },
];
